/**
 * AI Usage Tracking Service
 * 
 * Tracks AI Assistant character usage per user with monthly limits
 * based on subscription tier.
 * 
 * Free tier: 10,000 characters per month
 * Premium/Enterprise: Unlimited
 * 
 * @module services/aiUsageService
 */

import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import subscriptionService from './subscriptionService';

export interface AIUsageCheck {
  canUseAI: boolean;
  charactersUsed: number;
  charactersRemaining: number;
  limit: number;
  resetDate: Date;
  isUnlimited: boolean;
}

export interface AIUsageInfo {
  current: number; // characters used
  limit: number; // character limit
  remaining: number; // characters remaining
  resetDate: Date;
  isUnlimited: boolean;
  tier: string;
  percentage: number; // 0-100
  displayCurrent: string; // e.g. "3.4k"
  displayLimit: string; // e.g. "10k"
  displayRemaining: string; // e.g. "6.6k"
}

class AIUsageService {
  // Admin accounts with unlimited AI usage
  private adminUserIds = [
    'BSwYWa6AeggjuVTKS0hG3dRxtN03', // Abdul Antonio
  ];

  // Free tier character limit per month
  private readonly FREE_TIER_LIMIT = 10000;

  // Track if we've already shown the index warning (only show once per session)
  private hasShownIndexWarning = false;

  /**
   * Check if user is admin (bypass all limits)
   */
  private async isAdminUser(userId: string): Promise<boolean> {
    return this.adminUserIds.includes(userId);
  }

  /**
   * Get the start of the next month
   */
  private getNextMonthStart(): Date {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth;
  }

  /**
   * Get the start of the current month
   */
  private getCurrentMonthStart(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }

  /**
   * Format number to readable string (e.g. 3456 → "3.5k", 850 → "850")
   */
  private formatCharacters(num: number): string {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  }

  /**
   * Get total characters used this month for a user
   */
  async getMonthlyCharacterUsage(userId: string): Promise<number> {
    try {
      const monthStart = this.getCurrentMonthStart();
      const monthStartTimestamp = Timestamp.fromDate(monthStart);

      const usageQuery = query(
        collection(db, 'aiUsage'),
        where('userId', '==', userId),
        where('timestamp', '>=', monthStartTimestamp)
      );

      const snapshot = await getDocs(usageQuery);
      
      let totalCharacters = 0;
      snapshot.forEach((doc) => {
        const data = doc.data();
        totalCharacters += data.characters || 0;
      });

      return totalCharacters;
    } catch (error: any) {
      // Check if this is a Firebase index error
      const isIndexError = error?.message?.includes('index') || error?.code === 'failed-precondition';
      
      if (isIndexError && !this.hasShownIndexWarning) {
        this.hasShownIndexWarning = true;
        console.warn(
          '⚠️ Firebase Index Required for AI Usage Tracking\n' +
          'Please create the composite index at:\n' +
          'https://console.firebase.google.com/v1/r/project/superkonnectedwebsite/firestore/indexes\n' +
          'Collection: aiUsage\n' +
          'Fields: userId (Ascending), timestamp (Ascending)\n' +
          'This warning will only be shown once per session.'
        );
      } else if (!isIndexError) {
        // Only log non-index errors
        console.error('Error getting monthly character usage:', error);
      }
      
      // Return 0 to allow the app to continue functioning
      return 0;
    }
  }

  /**
   * Track character usage for an AI interaction
   */
  async trackCharacterUsage(userId: string, characters: number): Promise<void> {
    try {
      await addDoc(collection(db, 'aiUsage'), {
        userId,
        characters,
        timestamp: Timestamp.now(),
        type: 'interaction'
      });
      
      console.log(`📊 Tracked ${characters} characters for user ${userId.substring(0, 8)}...`);
    } catch (error) {
      console.error('Error tracking character usage:', error);
    }
  }

  /**
   * Check if user can use AI Assistant based on character limits
   */
  async checkAIUsage(userId: string): Promise<AIUsageCheck> {
    try {
      // ✅ Admin users have unlimited AI usage
      const isAdmin = await this.isAdminUser(userId);
      if (isAdmin) {
        return {
          canUseAI: true,
          charactersUsed: 0,
          charactersRemaining: -1,
          limit: -1,
          resetDate: this.getNextMonthStart(),
          isUnlimited: true
        };
      }

      // Get user subscription
      const subscription = await subscriptionService.getUserSubscription(userId);
      
      if (!subscription) {
        // Default to free tier if no subscription found
        const used = await this.getMonthlyCharacterUsage(userId);
        return {
          canUseAI: used < this.FREE_TIER_LIMIT,
          charactersUsed: used,
          charactersRemaining: Math.max(0, this.FREE_TIER_LIMIT - used),
          limit: this.FREE_TIER_LIMIT,
          resetDate: this.getNextMonthStart(),
          isUnlimited: false
        };
      }

      // Check if user has unlimited AI (premium/enterprise with aiFeatures enabled)
      if (subscription.limitations.aiFeatures) {
        return {
          canUseAI: true,
          charactersUsed: 0,
          charactersRemaining: -1,
          limit: -1,
          resetDate: this.getNextMonthStart(),
          isUnlimited: true
        };
      }

      // Free tier - 10,000 characters per month
      const charactersUsed = await this.getMonthlyCharacterUsage(userId);
      const charactersRemaining = Math.max(0, this.FREE_TIER_LIMIT - charactersUsed);
      const canUseAI = charactersUsed < this.FREE_TIER_LIMIT;

      return {
        canUseAI,
        charactersUsed,
        charactersRemaining,
        limit: this.FREE_TIER_LIMIT,
        resetDate: this.getNextMonthStart(),
        isUnlimited: false
      };
    } catch (error) {
      console.error('Error checking AI usage:', error);
      return {
        canUseAI: false,
        charactersUsed: 0,
        charactersRemaining: 0,
        limit: this.FREE_TIER_LIMIT,
        resetDate: this.getNextMonthStart(),
        isUnlimited: false
      };
    }
  }

  /**
   * Get AI usage information for display
   */
  async getAIUsageInfo(userId: string): Promise<AIUsageInfo> {
    try {
      const isAdmin = await this.isAdminUser(userId);
      
      if (isAdmin) {
        return {
          current: 0,
          limit: -1,
          remaining: -1,
          resetDate: this.getNextMonthStart(),
          isUnlimited: true,
          tier: 'admin',
          percentage: 0,
          displayCurrent: '0',
          displayLimit: '∞',
          displayRemaining: '∞'
        };
      }

      const subscription = await subscriptionService.getUserSubscription(userId);
      const tier = subscription?.tier || 'free';

      // Premium/Enterprise with AI features = unlimited
      if (subscription?.limitations.aiFeatures) {
        return {
          current: 0,
          limit: -1,
          remaining: -1,
          resetDate: this.getNextMonthStart(),
          isUnlimited: true,
          tier,
          percentage: 0,
          displayCurrent: '0',
          displayLimit: '∞',
          displayRemaining: '∞'
        };
      }

      // Free tier - 10,000 characters
      const current = await this.getMonthlyCharacterUsage(userId);
      const remaining = Math.max(0, this.FREE_TIER_LIMIT - current);
      const percentage = Math.min(100, (current / this.FREE_TIER_LIMIT) * 100);

      return {
        current,
        limit: this.FREE_TIER_LIMIT,
        remaining,
        resetDate: this.getNextMonthStart(),
        isUnlimited: false,
        tier,
        percentage,
        displayCurrent: this.formatCharacters(current),
        displayLimit: this.formatCharacters(this.FREE_TIER_LIMIT),
        displayRemaining: this.formatCharacters(remaining)
      };
    } catch (error) {
      console.error('Error getting AI usage info:', error);
      return {
        current: 0,
        limit: this.FREE_TIER_LIMIT,
        remaining: this.FREE_TIER_LIMIT,
        resetDate: this.getNextMonthStart(),
        isUnlimited: false,
        tier: 'free',
        percentage: 0,
        displayCurrent: '0',
        displayLimit: '10k',
        displayRemaining: '10k'
      };
    }
  }

  /**
   * Check if user has enough characters remaining for estimated usage
   */
  async canUseCharacters(userId: string, estimatedCharacters: number = 500): Promise<{
    allowed: boolean;
    reason?: string;
    usage: AIUsageCheck;
  }> {
    const usage = await this.checkAIUsage(userId);

    if (!usage.canUseAI) {
      return {
        allowed: false,
        reason: 'limit_reached',
        usage
      };
    }

    // Check if estimated usage would exceed limit
    if (!usage.isUnlimited && usage.charactersRemaining < estimatedCharacters) {
      return {
        allowed: false,
        reason: 'insufficient_characters',
        usage
      };
    }

    return {
      allowed: true,
      usage
    };
  }
}

export const aiUsageService = new AIUsageService();
